import { useMemo, useEffect, useState } from 'react'
import React from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  MenuItem,
  TextField as MuiTextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import Stack from '@mui/material/Stack'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import {
  TextContainer,
  StyledCard,
  CheckboxControlContainer,
  CheckboxFieldContainer,
} from '../../../styles'

/**
 * ProducerOtherDataDetails Form Component
 *
 * This component renders a form for entering additional contract details specific to producer contracts.
 * It captures administrative and legal information including:
 * - Contract number and status (active, inactive, in progress)
 * - Stadtwerke customer status and contract number (producer-specific)
 * - Sales tax liability
 * - Marketing consent
 * - Terms and conditions acceptance
 *
 * The component handles validation of required fields, including conditional validation
 * for Stadtwerke contract numbers when applicable. It ensures that terms and conditions
 * are accepted before allowing form submission.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.payload - The existing contract data to pre-populate the form
 * @param {Function} props.setPayloadThree - Function to update the parent component's payload state
 * @param {Function} props.confirmPayload - Function called when form is successfully submitted
 * @param {Function} props.handleClose - Function to close the form dialog
 * @param {number} props.submit - Trigger for form submission (incremented to trigger)
 * @param {Function} props.setSubmit - Function to reset the submit counter
 * @param {Function} props.setCanNext - Function to enable/disable the next button based on form validity
 *
 * @example
 * <ProducerOtherDataDetails
 *   payload={contractData}
 *   setPayloadThree={handleSetPayload}
 *   confirmPayload={handleNext}
 *   handleClose={onClose}
 *   submit={submitValue}
 *   setSubmit={setSubmitValue}
 *   setCanNext={setCanNextValue}
 * />
 *
 * @returns {React.ReactElement} A form for entering additional producer contract details
 */
function OtherDataDetails({
  payload,
  setPayloadThree,
  confirmPayload,
  submit,
  setSubmit,
  setCanNext,
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (submit > 0) document.getElementById('subThree').click()
    setSubmit(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit])

  const initialValues = {
    contractNumber: payload?.contractNumber || '',
    contractType: payload?.contractType || '',
    contractStatus: payload?.contractStatus || '',
    isSalesTaxLiable: payload?.isSalesTaxLiable || false,
    isMarketingInformationConsentGiven:
      payload?.isMarketingInformationConsentGiven || false,
    isTermsAndConditionsAccepted:
      payload?.isTermsAndConditionsAccepted || false,
    isAlreadyClientOfUtility: payload.isAlreadyClientOfUtility || false,
    utilityContractNumber: payload.utilityContractNumber || '',
  }
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        contractNumber: Yup.string()
          .required(t('LBLContractNumber'))
          .label(t('LBLVertragsnummer')),
        contractStatus: Yup.string()
          .required(t('LBLContractStatusReq'))
          .label(t('LBLUserStatus')),
        isMarketingInformationConsentGiven: Yup.bool().label(
          'LBLMarketingInformation'
        ),
        isTermsAndConditionsAccepted: Yup.bool()
          .oneOf([true], t('LBLAgb'))
          .label('LBLAgbCondition'),
        isAlreadyClientOfUtility: Yup.bool().label('LBLStadtWerkeKunden'),
        utilityContractNumber: Yup.string()
          .nullable()
          .when('isAlreadyClientOfUtility', {
            is: (isAlreadyClientOfUtility) => isAlreadyClientOfUtility,
            then: () =>
              Yup.string()
                .nullable()
                .required(t('LBLCompulsory'))
                .label(t('LBLStadtWerkeKundenNr')),
          }),
      }),
    [t]
  )

  /** @type {import("formik").FormikConfig<typeof initialValues>['onSubmit']} */
  const handleSubmit = (values, actions) => {
    const payload = {
      contractNumber: values.contractNumber,
      isSalesTaxLiable: values?.isSalesTaxLiable || false,
      isMarketingInformationConsentGiven:
        values?.isMarketingInformationConsentGiven,
      isTermsAndConditionsAccepted: values?.isTermsAndConditionsAccepted,
      contractType: values?.contractType || '',
      contractStatus: values?.contractStatus,
      isAlreadyClientOfUtility: values.isAlreadyClientOfUtility || false,
      utilityContractNumber: values.utilityContractNumber || '',
    }
    setPayloadThree(payload)
    confirmPayload()
    actions.setSubmitting(false)
    actions.setTouched({}, false)
    actions.setErrors({})
    actions.setFieldError({})
    actions.setFieldTouched({}, false, false)
  }

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={handleSubmit}
      >
        {({ errors, touched, values, setFieldValue, isValid }) => (
          <Form noValidate>
            <>
              {setCanNext(touched && isValid)}
              <Stack
                ml={3}
                my={2}
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {values.isAlreadyClientOfUtility ? (
                    <Field
                      required
                      margin="none"
                      component={TextField}
                      id="utilityContractNumber"
                      name="utilityContractNumber"
                      label={t('LBLStadtWerkeKundenNr')}
                      InputProps={{
                        title: 'Bitte füllen Sie dieses Feld aus',
                      }}
                      sx={{ flex: 1 }}
                    />
                  ) : undefined}
                  <FormControlLabel
                    control={
                      <Field
                        component={Checkbox}
                        type="checkbox"
                        id="isAlreadyClientOfUtility"
                        name="isAlreadyClientOfUtility"
                        onChange={(event) => {
                          setFieldValue(
                            'isAlreadyClientOfUtility',
                            event.target.checked
                          )
                        }}
                        checked={values.isAlreadyClientOfUtility}
                      />
                    }
                    label={t('LBLStadtWerkeKundenAUW')}
                  />
                </div>
              </Stack>

              <Stack
                ml={3}
                my={5}
                direction="row"
                alignItems="center"
                spacing={8}
              >
                <Field
                  fullWidth
                  margin="none"
                  component={TextField}
                  id="contractNumber"
                  name="contractNumber"
                  label={t('Vertragsnummer')}
                  required
                  InputProps={{
                    title: 'Bitte füllen Sie dieses Feld aus',
                  }}
                  sx={{ width: '500px' }}
                />

                <MuiTextField
                  select
                  id="contractStatus"
                  name="contractStatus"
                  defaultValue={payload?.contractStatus}
                  value={values.contractStatus}
                  onChange={(event) =>
                    setFieldValue('contractStatus', event.target.value)
                  }
                  label={t('LBLContaractStatus')}
                  open={open}
                  onClose={() => setOpen(false)}
                  onOpen={() => setOpen(true)}
                  sx={{ width: '500px' }}
                >
                  <MenuItem value="ACTIVE">{t('LBLActive')}</MenuItem>
                  <MenuItem value="INACTIVE">{t('LBLExpired')}</MenuItem>
                  <MenuItem value="IN_PROGRESS">{t('LBLInreview')}</MenuItem>
                </MuiTextField>
              </Stack>
              <Stack my={5} direction="row" alignItems="center" spacing={8}>
                <FormControlLabel
                  control={
                    <Field
                      component={Checkbox}
                      type="checkbox"
                      id="isSalesTaxLiable"
                      name="isSalesTaxLiable"
                      onChange={(event) => {
                        setFieldValue('isSalesTaxLiable', event.target.checked)
                      }}
                      checked={values.isSalesTaxLiable}
                    />
                  }
                  label={t('LBLSalesTaxLiability')}
                />
              </Stack>

              {/* this is checkbox to skip the billing Address step in edit workglow. */}
              <Stack my={2} direction="row" alignItems="center" spacing={1}>
                <FormControlLabel
                  sx={{ width: '100%' }}
                  control={
                    <CheckboxControlContainer>
                      <CheckboxFieldContainer>
                        <Field
                          component={Checkbox}
                          type="checkbox"
                          id="isMarketingInformationConsentGiven"
                          name="isMarketingInformationConsentGiven"
                          onChange={(event) => {
                            setFieldValue(
                              'isMarketingInformationConsentGiven',
                              event.target.checked
                            )
                          }}
                          checked={values.isMarketingInformationConsentGiven}
                        />
                      </CheckboxFieldContainer>
                      <TextContainer>
                        <StyledCard
                          variant="outline"
                          text={t('LBLMarketingText')}
                        />
                      </TextContainer>
                    </CheckboxControlContainer>
                  }
                />
              </Stack>
              <Stack my={2} direction="row" alignItems="center" spacing={1}>
                <FormControlLabel
                  sx={{ width: '100%' }}
                  control={
                    <CheckboxControlContainer>
                      <CheckboxFieldContainer>
                        <Field
                          component={Checkbox}
                          type="checkbox"
                          id="isTermsAndConditionsAccepted"
                          name="isTermsAndConditionsAccepted"
                          onChange={(event) => {
                            setFieldValue(
                              'isTermsAndConditionsAccepted',
                              event.target.checked
                            )
                          }}
                          checked={values.isTermsAndConditionsAccepted}
                        />
                      </CheckboxFieldContainer>
                      <TextContainer>
                        <StyledCard
                          variant="outline"
                          text={t('LBLAgbText')}
                          required={t('LBLRequired')}
                          error={errors.isTermsAndConditionsAccepted}
                        />
                      </TextContainer>
                    </CheckboxControlContainer>
                  }
                />
              </Stack>
              <Button sx={{ display: 'none' }} id="subThree" type="submit" />
            </>
          </Form>
        )}
      </Formik>
    </>
  )
}

OtherDataDetails.propTypes = {
  payload: PropTypes.objectOf(PropTypes.any),
  setPayloadThree: PropTypes.func.isRequired,
  confirmPayload: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}

export default OtherDataDetails
